import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Link from '@components/shared/Link';
import { useAppContext } from '@/context/AppContext';
import NotifyMeButton from '@components/shared/NotifyMeButton';
import { getPagePath } from '@/utils/pagePaths';
import { useEditorFrame } from '@components/preview/editGates';
import { resolveDrawerFromTheme } from '@/theme/header-blocks';

// Helper to get slug from page (supports both old and new field names)
const getSlug = (page) => page?.slug || page?.url || '';
// Helper to get parentId from page (supports both old and new field names)
const getParentId = (page) => page?.parentId || page?.parentUrl || null;

const PageLink = ({ onClick, fullPath, name, casing }) => {
    // Menu links should always point to pages, never to images directly
    const to = `/${fullPath}`;
    const textTransform = !casing || casing === 'none' ? 'none' : casing;

    return (
        <Link
            onClick={onClick}
            to={to}
            className="drawer-link block hover:opacity-[var(--drawer-hover-opacity,0.7)] transition-opacity duration-200"
            style={{
                textTransform,
                color: 'var(--drawer-link-color, var(--color-side-menu-text))',
            }}
        >
            {name}
        </Link>
    );
};

PageLink.propTypes = {
    onClick: PropTypes.func,
    fullPath: PropTypes.string,
    name: PropTypes.string,
    casing: PropTypes.string,
};

function SideMenu({ pages, content, showMenu }) {
    const { toggleMenu, theme } = useAppContext();
    const inEditor = useEditorFrame();
    const pagesObj = content?.pages || {};
    const savedScrollRef = useRef(0);
    const library = content?.library
        || (typeof window !== 'undefined' ? window.__LIBRARY__ : null);
    const drawer = resolveDrawerFromTheme(theme, library);

    // Drawer pages follow Nav (showInNav), independent of header Menu (isPublic).
    // navSource may limit the list to top-level pages only.
    const menuSource = drawer.navSource;
    const menuPages = pages.filter((page) => (
        page.showInNav !== false
        && (menuSource === 'all-public' || !getParentId(page))
    ));

    const handleClick = () => {
        toggleMenu(false);
    };

    const drawerStyle = {
        display: showMenu ? undefined : 'none',
        fontFamily: drawer.fontFamily,
        fontWeight: drawer.weight || undefined,
        fontStyle: drawer.style || undefined,
        backgroundColor: drawer.background || undefined,
        '--drawer-size': `${drawer.size}em`,
        '--drawer-tracking': `${drawer.tracking}em`,
        ...(drawer.lineHeight != null ? { '--drawer-leading': String(drawer.lineHeight) } : {}),
        '--drawer-gap': `${drawer.gap}em`,
        '--drawer-align': drawer.align,
        '--drawer-hover-opacity': String(drawer.hoverOpacity),
        ...(drawer.color ? { '--drawer-link-color': drawer.color } : {}),
        ...(drawer.hoverColor ? { '--drawer-hover-color': drawer.hoverColor } : {}),
    };

    // Lock body scroll when menu is open (overflow: hidden + position: fixed to work on iOS)
    // Scroll restoration is handled by the global history state system
    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (showMenu) {
            const scrollY = window.scrollY ?? document.documentElement.scrollTop;
            savedScrollRef.current = scrollY;
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
            return () => {
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.left = '';
                document.body.style.right = '';
                document.body.style.width = '';
                // Restore scroll position when menu closes (without navigation)
                // Navigation scroll is handled by history state
                window.scrollTo(0, savedScrollRef.current);
            };
        }
    }, [showMenu]);

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: `
                /* Backdrop only covers the wrapper area, not the menu */
                .menu-backdrop {
                    display: none;
                    position: fixed;
                    top: 0;
                    left: 0;
                    bottom: 0;
                    right: min(400px, 85vw);
                    z-index: 200;
                    opacity: 0;
                    pointer-events: none;
                    cursor: pointer;
                    transition: opacity 0.3s ease;
                }
                .menu-backdrop.menu-backdrop-visible {
                    opacity: 1;
                    pointer-events: auto;
                }
                /* Menu is fixed on the right, underneath the sliding body */
                .floating-hamburger {
                    display: none;
                }
                #menu,
                .menu-panel {
                    display: none;
                    position: fixed;
                    top: 0;
                    right: 0;
                    bottom: 0;
                    width: min(400px, 85vw);
                    z-index: 50;
                }
                #menu .drawer-link {
                    font-size: var(--drawer-size, 1.4em);
                    letter-spacing: var(--drawer-tracking, 0.25em);
                    line-height: var(--drawer-leading, 2);
                    text-align: var(--drawer-align, center);
                    padding-top: calc(var(--drawer-gap, 1em) * 0.5);
                    padding-bottom: calc(var(--drawer-gap, 1em) * 0.5);
                    color: var(--drawer-link-color, var(--color-side-menu-text));
                    text-rendering: optimizeLegibility;
                    -webkit-font-smoothing: antialiased;
                }
                #menu .drawer-link:hover {
                    color: var(--drawer-hover-color, var(--drawer-link-color, var(--color-side-menu-text)));
                }
                @media (max-width: 600px) {
                    .menu-backdrop { display: block; }
                    .floating-hamburger { display: flex; }
                    #menu,
                    .menu-panel {
                        display: flex;
                        flex-direction: column;
                    }
                    /* Body slides left to reveal the menu underneath */
                    #app-container #wrapper {
                        position: relative;
                        /* Column and block fills reach the window edge; keep them off the menu beside the page. */
                        overflow-x: clip;
                        z-index: 100;
                        background: var(--color-background, #fff);
                        transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
                        box-shadow: none;
                    }
                    #app-container.show-menu #wrapper {
                        transform: translateX(calc(-1 * min(400px, 85vw)));
                        box-shadow: -8px 0 30px rgba(0, 0, 0, 0.15);
                    }
                }
                /* Appearance phone layer: iframe may be wider than 600px. */
                html[data-appearance-preview="1"][data-header-bp="phone"] .menu-backdrop {
                    display: block;
                }
                html[data-appearance-preview="1"][data-header-bp="phone"] #menu,
                html[data-appearance-preview="1"][data-header-bp="phone"] .menu-panel {
                    display: flex;
                    flex-direction: column;
                }
                html[data-appearance-preview="1"][data-header-bp="phone"] #app-container #wrapper {
                    position: relative;
                    /* Column and block fills reach the window edge; keep them off the menu beside the page. */
                    overflow-x: clip;
                    z-index: 100;
                    background: var(--color-background, #fff);
                    transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
                    box-shadow: none;
                }
                html[data-appearance-preview="1"][data-header-bp="phone"] #app-container.show-menu #wrapper {
                    transform: translateX(calc(-1 * min(400px, 85vw)));
                    box-shadow: -8px 0 30px rgba(0, 0, 0, 0.15);
                }
            `}} />
            {/* Floating hamburger — never in Appearance (MenuToggle is pickable); hidden when a MenuToggle block owns placement on the live site. */}
            {!inEditor && !drawer.hideFloatingToggle && (
                <button
                    className="floating-hamburger fixed top-5 right-5 z-[1100] w-10 h-10 cursor-pointer border-0 bg-transparent p-0 items-center justify-center"
                    onClick={() => toggleMenu()}
                    type="button"
                    aria-label="Toggle menu"
                >
                    <span
                        className="hamburger-icon block relative w-full h-full"
                        aria-hidden="true"
                    />
                </button>
            )}
            <div
                className={`menu-backdrop ${showMenu ? 'menu-backdrop-visible' : ''}`}
                onClick={handleClick}
                aria-hidden="true"
            />
            <aside
                id="menu"
                className="menu-panel bg-side-menu-background"
                style={drawerStyle}
                onTouchMove={(e) => e.preventDefault()}
                role="dialog"
                aria-label="Navigation menu"
                data-theme-path="colors.sideMenuBackground"
                data-header-drawer={drawer.toggleId}
            >

            <div
                className="wrapper mx-7 my-14 flex-grow flex flex-col justify-start"
            >
                <nav className="menu">
                    <ul
                        className="menu-container flex flex-col justify-center list-none p-0 m-0"
                        style={{ fontFamily: 'inherit' }}
                        data-theme-path="menu.navSource"
                        data-header-drawer={drawer.toggleId}
                    >
                        {content?.home?.showInNav !== false && (
                            <li>
                                <Link
                                    onClick={handleClick}
                                    to="/"
                                    className="drawer-link block hover:opacity-[var(--drawer-hover-opacity,0.7)] transition-opacity duration-200"
                                    style={{
                                        textTransform: drawer.casing === 'none' ? 'none' : drawer.casing,
                                    }}
                                >
                                    {content?.home?.name || 'Home'}
                                </Link>
                            </li>
                        )}
                        {menuPages.map((page) => {
                            const fullPath = getPagePath(page, pagesObj);
                            return (
                                <li key={getSlug(page) || page.id}>
                                    <PageLink
                                        onClick={handleClick}
                                        name={page.name}
                                        fullPath={fullPath}
                                        casing={drawer.casing}
                                    />
                                </li>
                            );
                        })}
                    </ul>
                </nav>

                {/* Visitor notification opt-in. Renders nothing unless the site has
                    enabled it, and never prompts until it is pressed. */}
                <div
                    className="mt-8 text-side-menu-text"
                    style={{ textAlign: drawer.align }}
                >
                    <NotifyMeButton />
                </div>
            </div>
        </aside>
        </>
    );
}

SideMenu.propTypes = {
    pages: PropTypes.array,
    content: PropTypes.object,
    showMenu: PropTypes.bool,
};

export default SideMenu;
